import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { player } from '../../game/player';
import { boxes } from '../../game/streetCollision';
import { baseGroundY } from '../../city/cityGround';
import Helicopter, { useHeliKit } from './Helicopter';
import HeliPassengers from './HeliPassengers';
import { HELIPORT, BOARD_NEAR, SEATS, useHeliTour, loadPilots, padPose, padNumber, heliNow } from '../../city/heliTour';

// THE HELIPORT (city/heliTour; data/heli_routes.json) — the top-left lot, where
// Khari pointed: four pads in a 2×2 on a painted apron, a booth at the corner
// by the roads (the way in) under an electronic sign, a windsock, and a
// helicopter on every pad. A helicopter is where its pad's flight says it is
// (padPose — on the pad, or in the air on the server's clock), so everyone in
// the street sees the same one lift off and come home. Parked, its fuselage is
// solid (streetCollision `boxes`); in the air, not.
//
// Standing at a pad (BOARD_NEAR of its centre) is what HeliPanel offers boarding
// from (useHeliTour `near`). Each pad's pilot (a cast member, assigned in Web
// /admin/cast — city/heliTour usePilots) stands in front of their helicopter's
// nose while it is down, walks out to it when people board, and flies it from the
// front seat (HeliPassengers PilotCrew).
const PAD_R = 3.3;
const GOLD = '#f2b632';

function padTexture(n) {
  const c = document.createElement('canvas'); c.width = 512; c.height = 512;
  const x = c.getContext('2d');
  x.fillStyle = '#3b3f45'; x.beginPath(); x.arc(256, 256, 250, 0, Math.PI * 2); x.fill();
  x.strokeStyle = GOLD; x.lineWidth = 22; x.beginPath(); x.arc(256, 256, 222, 0, Math.PI * 2); x.stroke();
  x.strokeStyle = '#f5f5f0'; x.lineWidth = 6; x.beginPath(); x.arc(256, 256, 196, 0, Math.PI * 2); x.stroke();
  x.fillStyle = '#f5f5f0'; x.font = 'bold 250px system-ui, Arial'; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText('H', 256, 268);
  x.fillStyle = GOLD; x.font = 'bold 54px system-ui, Arial'; x.fillText(String(n), 256, 440);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}

function Pad({ pad, y }) {
  const n = padNumber(pad.id);
  const tex = useMemo(() => padTexture(n), [n]);
  useEffect(() => () => tex.dispose(), [tex]);
  return (
    <group position={[pad.x, y, pad.z]}>
      <mesh rotation={[-Math.PI / 2, 0, -pad.rotY]} receiveShadow>
        <circleGeometry args={[PAD_R, 48]} />
        <meshStandardMaterial map={tex} roughness={0.9} polygonOffset polygonOffsetFactor={-2} />
      </mesh>
      {/* Edge lights: amber studs round the rim. */}
      {Array.from({ length: 10 }, (_, i) => {
        const a = (i / 10) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(a) * (PAD_R + 0.15), 0.05, Math.sin(a) * (PAD_R + 0.15)]}>
            <sphereGeometry args={[0.07, 8, 6]} />
            <meshStandardMaterial color="#ffb733" emissive="#ff9a1a" emissiveIntensity={1.6} />
          </mesh>
        );
      })}
    </group>
  );
}

function Apron({ y }) {
  // A painted border round the four pads.
  const xs = HELIPORT.pads.map((p) => p.x); const zs = HELIPORT.pads.map((p) => p.z);
  const x0 = Math.min(...xs) - PAD_R - 2.2; const x1 = Math.max(...xs) + PAD_R + 2.2;
  const z0 = Math.min(...zs) - PAD_R - 2.2; const z1 = Math.max(...zs) + PAD_R + 2.2;
  const w = 0.22;
  const lines = [
    [(x0 + x1) / 2, z0, x1 - x0, w], [(x0 + x1) / 2, z1, x1 - x0, w],
    [x0, (z0 + z1) / 2, w, z1 - z0], [x1, (z0 + z1) / 2, w, z1 - z0],
  ];
  return (
    <group position={[0, y - 0.005, 0]}>
      {lines.map(([cx, cz, lx, lz], i) => (
        <mesh key={i} position={[cx, 0, cz]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[lx, lz]} />
          <meshStandardMaterial color={GOLD} roughness={0.85} polygonOffset polygonOffsetFactor={-1} />
        </mesh>
      ))}
    </group>
  );
}

// ── THE SIGN: an electronic board over the booth ─────────────────────────────
// Khari, 2026-11-01: "improve the signage and remove the credits. I would
// prefer an LCD/electronic sign with a graphic on the bottom half. Please
// design a generic placement. I can replace it with an image model if needed."
// One canvas, drawn like a lit screen: the top half is the board — the name,
// the flights, and each pad's state live (ready · boarding 2/5 · in the air,
// back in 0:42), redrawn only when it changes; the bottom half is the graphic
// — a night skyline with a helicopter on its route, drawn here, or the picture
// at heliport.sign.image (data/heli_routes.json) when one is set. A fine pixel
// grid over the lot makes it read as a screen. (The model's credit lives in
// DOCS/WORLD/ATTRIBUTION.md now.)
const SIGN_W = 2.6; const SIGN_H = 1.5;
const CW = 1040; const CH = 600;

function mulberry(a) { return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** The bottom half's default graphic: dusk over the city, a helicopter on a dashed route. */
function drawGraphic(x, top, w, h) {
  const sky = x.createLinearGradient(0, top, 0, top + h);
  sky.addColorStop(0, '#0b1230'); sky.addColorStop(0.55, '#3a1d5c'); sky.addColorStop(0.85, '#c2456b'); sky.addColorStop(1, '#f2a65a');
  x.fillStyle = sky; x.fillRect(0, top, w, h);
  const r = mulberry(7);
  for (let i = 0; i < 40; i++) { x.fillStyle = `rgba(255,255,255,${0.3 + r() * 0.5})`; x.fillRect(r() * w, top + r() * h * 0.45, 2, 2); }
  // The skyline, two depths.
  const skyline = (base, hMin, hMax, color, lit) => {
    let bx = -10;
    while (bx < w) {
      const bw = 26 + r() * 46; const bh = hMin + r() * (hMax - hMin);
      x.fillStyle = color; x.fillRect(bx, top + h - base - bh, bw, bh + base);
      if (lit) for (let wy = top + h - base - bh + 8; wy < top + h - 12; wy += 12) for (let wx = bx + 5; wx < bx + bw - 6; wx += 10) if (r() < 0.32) { x.fillStyle = r() < 0.7 ? '#ffd27a' : '#9fd6ff'; x.fillRect(wx, wy, 4, 5); }
      bx += bw + 2 + r() * 6;
    }
  };
  skyline(0, h * 0.25, h * 0.5, '#1c1636', false);
  skyline(0, h * 0.15, h * 0.38, '#0d0a1c', true);
  // The route: a dashed arc over the roofs.
  x.save(); x.setLineDash([10, 9]); x.lineWidth = 3; x.strokeStyle = 'rgba(242,182,50,0.85)';
  x.beginPath(); x.moveTo(w * 0.08, top + h * 0.78); x.bezierCurveTo(w * 0.3, top + h * 0.1, w * 0.7, top + h * 0.12, w * 0.92, top + h * 0.6); x.stroke(); x.restore();
  // The helicopter, in silhouette with its beacon, near the top of the arc.
  const hx = w * 0.58; const hy = top + h * 0.26; const k = h / 280;
  x.save(); x.translate(hx, hy); x.scale(k, k); x.rotate(-0.08); x.fillStyle = '#05060c';
  x.beginPath(); x.ellipse(0, 0, 46, 22, 0, 0, Math.PI * 2); x.fill();
  x.fillRect(-120, -6, 90, 9); x.fillRect(-128, -26, 10, 30);
  x.fillRect(-4, -34, 8, 14); x.fillRect(-95, -36, 190, 4);
  x.fillRect(-30, 26, 64, 4); x.fillRect(-22, 18, 4, 10); x.fillRect(20, 18, 4, 10);
  x.fillStyle = 'rgba(160,210,255,0.55)'; x.beginPath(); x.ellipse(22, -4, 18, 12, 0, 0, Math.PI * 2); x.fill();
  x.fillStyle = '#ff3b30'; x.beginPath(); x.arc(-124, -28, 5, 0, Math.PI * 2); x.fill();
  x.restore();
}

function useSignTexture(image) {
  const pads = useHeliTour((s) => s.pads);
  const [, tick] = React.useState(0);
  useEffect(() => { const iv = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(iv); }, []);
  const { canvas, tex, ctx, img } = useMemo(() => {
    const c = document.createElement('canvas'); c.width = CW; c.height = CH;
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    const im = { el: null, ok: false };
    if (image) {
      const el = new Image(); el.crossOrigin = 'anonymous';
      el.onload = () => { im.ok = true; t.needsUpdate = true; };
      el.src = image; im.el = el;
    }
    return { canvas: c, tex: t, ctx: c.getContext('2d'), img: im };
  }, [image]);
  useEffect(() => () => tex.dispose(), [tex]);
  // The board's live line: each pad, now.
  const now = Date.now();   // the pads' times are on this browser's clock (worldSocket toLocal)
  const status = HELIPORT.pads.map((p, i) => {
    const s = pads[p.id];
    if (s?.state === 'flying') return `PAD ${i + 1} · IN THE AIR${s.endsAt ? ` · ${Math.max(0, Math.ceil((s.endsAt - now) / 1000))}s` : ''}`;
    if (s?.state === 'boarding') return `PAD ${i + 1} · BOARDING ${(s.seats || []).length}/${SEATS}`;
    return `PAD ${i + 1} · READY`;
  });
  const key = `${status.join('|')}|${img.ok}`;
  const last = useRef('');
  if (key !== last.current) {
    last.current = key;
    const x = ctx; const half = CH * 0.5;
    x.fillStyle = '#05070c'; x.fillRect(0, 0, CW, CH);
    // TOP: the board.
    x.textAlign = 'center'; x.textBaseline = 'alphabetic';
    x.shadowColor = 'rgba(242,182,50,0.8)'; x.shadowBlur = 18; x.fillStyle = GOLD;
    x.font = '700 30px system-ui, Arial'; x.fillText('H Y P E T O W N', CW / 2, 52);
    x.font = '800 92px system-ui, Arial'; x.fillText('HELLA COPTERS', CW / 2, 140);   // the name (Khari, 2026-11-05: "Hella Copters")
    x.shadowColor = 'rgba(160,220,255,0.6)'; x.shadowBlur = 10; x.fillStyle = '#dff2ff';
    x.font = '600 28px system-ui, Arial'; x.fillText('3 FLIGHTS OVER THE CITY  ·  90 SECONDS  ·  5 SEATS', CW / 2, 186);
    x.shadowBlur = 8;
    status.forEach((line, i) => {
      const col = i % 2; const row = Math.floor(i / 2);
      x.fillStyle = /READY/.test(line) ? '#5dff9b' : /BOARDING/.test(line) ? '#ffd166' : '#ff8a6b';
      x.shadowColor = x.fillStyle;
      x.font = '700 24px ui-monospace, Menlo, monospace';
      x.fillText(line, CW * (col ? 0.73 : 0.27), 228 + row * 32);
    });
    x.shadowBlur = 0;
    x.fillStyle = 'rgba(242,182,50,0.6)'; x.fillRect(40, half - 3, CW - 80, 2);
    // BOTTOM: the graphic (the placement): the set image, else the drawn one.
    if (img.ok && img.el) {
      const iw = img.el.naturalWidth || 1; const ih = img.el.naturalHeight || 1;
      const sc = Math.max(CW / iw, half / ih); const dw = iw * sc; const dh = ih * sc;
      x.save(); x.beginPath(); x.rect(0, half, CW, half); x.clip();
      x.drawImage(img.el, (CW - dw) / 2, half + (half - dh) / 2, dw, dh); x.restore();
    } else {
      drawGraphic(x, half, CW, half);
      x.textAlign = 'right'; x.fillStyle = 'rgba(255,255,255,0.85)'; x.font = '700 22px system-ui, Arial';
      x.fillText('WALK UP TO ANY PAD TO BOARD  ›', CW - 28, CH - 22);
    }
    // The screen: a fine pixel grid and a soft vignette.
    x.fillStyle = 'rgba(0,0,0,0.22)';
    for (let gy = 0; gy < CH; gy += 3) x.fillRect(0, gy, CW, 1);
    for (let gx = 0; gx < CW; gx += 3) x.fillRect(gx, 0, 1, CH);
    const vg = x.createRadialGradient(CW / 2, CH / 2, CH * 0.35, CW / 2, CH / 2, CW * 0.62);
    vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, 'rgba(0,0,0,0.35)');
    x.fillStyle = vg; x.fillRect(0, 0, CW, CH);
    tex.needsUpdate = true;
  }
  return tex;
}

function Booth() {
  const b = HELIPORT.booth;
  const y = baseGroundY(b.x, b.z);
  const tex = useSignTexture(HELIPORT.sign?.image || null);
  const signY = 1.3 + SIGN_H / 2;
  return (
    <group position={[b.x, y, b.z]} rotation={[0, b.rotY, 0]}>
      {/* The kiosk. */}
      <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.2, 1.1, 1.2]} />
        <meshStandardMaterial color="#1d2b3a" roughness={0.6} />
      </mesh>
      <mesh position={[0, 1.13, 0.05]} castShadow>
        <boxGeometry args={[2.5, 0.06, 1.5]} />
        <meshStandardMaterial color={GOLD} roughness={0.5} />
      </mesh>
      {/* The screen on two posts: a bezel, the lit face, a dark back. */}
      {[-SIGN_W / 2 + 0.12, SIGN_W / 2 - 0.12].map((x) => (
        <mesh key={x} position={[x, signY / 2, 0.34]} castShadow>
          <boxGeometry args={[0.08, signY, 0.08]} />
          <meshStandardMaterial color="#2a2f38" metalness={0.6} roughness={0.35} />
        </mesh>
      ))}
      <mesh position={[0, signY, 0.42]} castShadow>
        <boxGeometry args={[SIGN_W + 0.14, SIGN_H + 0.14, 0.1]} />
        <meshStandardMaterial color="#0c0f14" metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, signY, 0.475]}>
        <planeGeometry args={[SIGN_W, SIGN_H]} />
        <meshBasicMaterial map={tex} toneMapped={false} />
      </mesh>
      {/* A light strip along the bezel's top, so it reads lit at night. */}
      <mesh position={[0, signY + SIGN_H / 2 + 0.075, 0.47]}>
        <boxGeometry args={[SIGN_W + 0.1, 0.02, 0.02]} />
        <meshBasicMaterial color={GOLD} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Windsock() {
  const x = HELIPORT.centre[0] - 14; const z = HELIPORT.centre[1] + 13;
  const y = baseGroundY(x, z);
  const sock = useRef(null);
  useFrame(({ clock }) => { if (sock.current) sock.current.rotation.y = 0.9 + Math.sin(clock.elapsedTime * 0.4) * 0.35; });
  return (
    <group position={[x, y, z]}>
      <mesh position={[0, 1.6, 0]}><cylinderGeometry args={[0.04, 0.05, 3.2, 8]} /><meshStandardMaterial color="#d9dde3" metalness={0.5} roughness={0.4} /></mesh>
      <group ref={sock} position={[0, 3.1, 0]}>
        <mesh position={[0, 0, 0.55]} rotation={[Math.PI / 2 - 0.25, 0, 0]}>
          <coneGeometry args={[0.2, 1.1, 12, 1, true]} />
          <meshStandardMaterial color="#ff6a1a" side={THREE.DoubleSide} roughness={0.8} />
        </mesh>
      </group>
    </group>
  );
}

/** A pad's helicopter, and its fuselage solid while it is down. */
function PadHeli({ pad }) {
  const kit = useHeliKit();
  const state = useHeliTour((s) => s.pads[pad.id]?.state || 'idle');
  const poseOf = useMemo(() => () => padPose(pad.id, heliNow()), [pad.id]);   // the camera's clock (heliTour heliNow)
  useEffect(() => {
    if (state === 'flying') return undefined;
    const b = { cx: pad.x, cz: pad.z, hx: kit.size.x / 2 + 0.1, hz: kit.size.z * 0.36, ux: Math.cos(pad.rotY), uz: -Math.sin(pad.rotY), owner: `heli:${pad.id}` };
    boxes.push(b);
    return () => { const i = boxes.indexOf(b); if (i >= 0) boxes.splice(i, 1); };
  }, [state, pad.id, pad.x, pad.z, pad.rotY, kit]);
  return <Helicopter poseOf={poseOf}><HeliPassengers padId={pad.id} /></Helicopter>;
}

/** Which pad you are standing at (for HeliPanel), checked a few times a second. */
function NearPads() {
  const camera = useThree((s) => s.camera);
  const acc = useRef(0);
  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < 0.2) return;
    acc.current = 0;
    const ws = useWorldStore.getState();
    const hu = useHeliTour.getState();
    let near = null;
    if (ws.zoom === ZOOM.STREET && !ws.interior && !hu.mine) {
      const px = player.third ? player.x : camera.position.x;
      const pz = player.third ? player.z : camera.position.z;
      let best = BOARD_NEAR;
      for (const p of HELIPORT.pads) { const d = Math.hypot(px - p.x, pz - p.z); if (d < best) { best = d; near = p.id; } }
    }
    if (near !== hu.near) useHeliTour.setState({ near });
  });
  return null;
}

export default function Heliport() {
  const y = baseGroundY(HELIPORT.centre[0], HELIPORT.centre[1]) + 0.02;
  useEffect(() => { loadPilots(); }, []);
  return (
    <group>
      <Apron y={y} />
      {HELIPORT.pads.map((p) => <Pad key={p.id} pad={p} y={baseGroundY(p.x, p.z) + 0.02} />)}
      <Booth />
      <Windsock />
      <Suspense fallback={null}>
        {HELIPORT.pads.map((p) => <PadHeli key={p.id} pad={p} />)}
      </Suspense>
      <NearPads />
    </group>
  );
}
